<!-- Dialog title bar (dialog mode only) -->
<app-entity-mat-dialog-title *ngIf="!embedded"
  cdkDrag cdkDragRootElement=".cdk-overlay-pane" cdkDragHandle
  [title]="dialogTitle">
</app-entity-mat-dialog-title>

<!-- Content wrapper: mat-dialog-content in dialog mode, plain div in embedded mode -->
<mat-dialog-content *ngIf="!embedded">
  <ng-container *ngTemplateOutlet="contentTpl"></ng-container>
</mat-dialog-content>

<div *ngIf="embedded">
  <ng-container *ngTemplateOutlet="contentTpl"></ng-container>
</div>

<!-- Shared content template -->
<ng-template #contentTpl>

  <!-- Loading -->
  <div *ngIf="isLoading" class="standing-dialog-loading">
    <mat-spinner diameter="32"></mat-spinner>
    <span>Loading standing series...</span>
  </div>

  <!-- Error -->
  <div *ngIf="error" class="standing-dialog-error">
    <mat-icon>error</mat-icon>
    {{ error }}
  </div>

  <!-- Series Details -->
  <div *ngIf="series && !isLoading && !error">

    <!-- ====== SUMMARY VIEW (dialog only) ====== -->
    <div *ngIf="!embedded && !isEditing && !showDeleteSingleConfirm && !showDeactivateConfirm" class="standing-dialog-summary">
      <div class="summary-row">
        <strong>Schedule:</strong>
        {{ formatDays(series.days) }} @ {{ formatTime(series.time) }}
      </div>
      <div class="summary-row">
        <strong>Instructor:</strong>
        {{ getInstructorName(series.instructor_id) }}
      </div>
      <div class="summary-row">
        <strong>Duration:</strong> {{ series.duration }}m
      </div>
      <div class="summary-row">
        <strong>Frequency:</strong> {{ formatFrequency(series) }}
      </div>
      <div class="summary-row">
        <strong>End:</strong> {{ formatEndMode(series) }}
      </div>
      <div class="summary-row" *ngIf="series.events?.length > 0">
        <strong>Events:</strong> {{ series.events.length }}
      </div>
    </div>

    <!-- ====== DELETE SINGLE EVENT CONFIRMATION (dialog only) ====== -->
    <div *ngIf="!embedded && showDeleteSingleConfirm" class="delete-single-section">
      <div class="delete-single-header">
        <mat-icon>warning</mat-icon>
        <strong>Delete this single event?</strong>
      </div>
      <p>This will remove only this one event from the series. Other events in the series are not affected.</p>

      <div *ngIf="editError" class="standing-dialog-error">
        <mat-icon>error</mat-icon>
        {{ editError }}
      </div>

      <div class="delete-single-actions">
        <button type="button" mat-raised-button color="warn"
                [disabled]="isDeletingSingle"
                (click)="confirmDeleteSingle()">
          {{ isDeletingSingle ? 'Deleting...' : 'Delete Event' }}
        </button>
        <button type="button" mat-raised-button
                [disabled]="isDeletingSingle"
                (click)="cancelDeleteSingle()">
          Cancel
        </button>
      </div>
    </div>

    <!-- ====== DEACTIVATE SERIES CONFIRMATION (dialog only) ====== -->
    <div *ngIf="!embedded && showDeactivateConfirm" class="deactivate-confirm-section">
      <div class="deactivate-confirm-header">
        <mat-icon>warning</mat-icon>
        <strong>Delete this entire standing series?</strong>
      </div>
      <p>This will deactivate the series. No new events will be generated.</p>
      <mat-checkbox [(ngModel)]="deactivateDeleteFuture">
        Also delete all future unposted events
      </mat-checkbox>

      <div *ngIf="editError" class="standing-dialog-error">
        <mat-icon>error</mat-icon>
        {{ editError }}
      </div>
      <div class="deactivate-confirm-actions">
        <button type="button" mat-raised-button color="warn"
                [disabled]="isDeactivating"
                (click)="confirmDeactivate()">
          {{ isDeactivating ? 'Deleting...' : 'Delete Series' }}
        </button>
        <button type="button" mat-raised-button
                [disabled]="isDeactivating"
                (click)="cancelDeactivate()">
          Cancel
        </button>
      </div>
    </div>

    <!-- ====== EDIT / CONFIGURE MODE (shared) ====== -->
    <div *ngIf="isEditing" class="standing-dialog-edit">

      <!-- Context header: configure mode (new series), dialog only -->
      <div *ngIf="isConfigureMode && !embedded" class="configure-context-header">
        <div class="configure-context-line1">New Standing Series</div>
        <div class="configure-context-line2">Configure the schedule below, then click Generate.</div>
      </div>

      <!-- Context header: edit mode (existing series), dialog only -->
      <div *ngIf="!isConfigureMode && !embedded && originalSeries" class="edit-context-header">
        <div class="edit-context-line1">
          Editing: {{ originalSeries.days }} @ {{ originalSeries.time }}, {{ originalSeries.instructor }}
        </div>
        <div class="edit-context-line2">
          {{ originalSeries.frequency }} | {{ originalSeries.eventCount }} event{{ originalSeries.eventCount !== 1 ? 's' : '' }}
        </div>
      </div>

      <div *ngIf="editError" class="standing-dialog-error">
        <mat-icon>error</mat-icon>
        {{ editError }}
      </div>

      <!-- Conflict alert banner -->
      <div *ngIf="hasConflicts" class="conflict-alert-banner">
        <div class="conflict-alert-header">
          <mat-icon>warning</mat-icon>
          <strong>{{ conflicts.length }} scheduling conflict{{ conflicts.length !== 1 ? 's' : '' }} detected</strong>
          <button mat-icon-button class="conflict-dismiss-btn" (click)="dismissConflicts()" matTooltip="Dismiss">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <span class="conflict-alert-hint">Conflicting events are highlighted in red below. Click on them to reschedule or delete.</span>
      </div>

      <!-- Structural change warning (edit mode only) -->
      <div *ngIf="hasStructuralChanges() && !isConfigureMode" class="structural-warning">
        <mat-icon>info</mat-icon>
        <span>Changing schedule, frequency, or end date will delete all future unposted events and regenerate them.</span>
      </div>

      <!-- ====== Two-column layout ====== -->
      <div class="row">

        <!-- Left column: config fields -->
        <div class="col-md-6">

          <!-- Schedule: Days -->
          <div class="edit-section">
            <label class="edit-section-label">Days</label>
            <div class="day-toggle-section">
              <button *ngFor="let day of dayOptions"
                      type="button"
                      class="day-toggle-btn"
                      [class.selected]="isDaySelected(day.value)"
                      (click)="toggleDay(day.value)">
                {{ day.label }}
                <span *ngIf="hasDayOverride(day.value)" class="day-override-dot"></span>
              </button>
            </div>
          </div>

          <!-- Schedule: Time + Duration -->
          <div class="edit-section">
            <div class="edit-fields-row">
              <mat-form-field style="width: 150px">
                <mat-label>Time</mat-label>
                <mat-select [(ngModel)]="editChanges.time" (ngModelChange)="updateLivePreview()">
                  <mat-option *ngFor="let t of timeOptions" [value]="t.value">{{ t.label }}</mat-option>
                </mat-select>
              </mat-form-field>

              <mat-form-field style="width: 130px">
                <mat-label>Duration</mat-label>
                <mat-select [(ngModel)]="editChanges.duration" (ngModelChange)="updateLivePreview()">
                  <mat-option *ngFor="let element of _fieldsService.durations"
                              [value]="element.value">{{ element.value }}</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <!-- Instructor -->
          <div class="edit-section">
            <label class="edit-section-label">Instructor</label>
            <div class="edit-fields-row">
              <mat-form-field style="width: 220px">
                <mat-label>Primary Instructor</mat-label>
                <mat-select [(ngModel)]="editChanges.instructor" (ngModelChange)="updateLivePreview()">
                  <ng-container *ngFor="let element of sortedInstructors">
                    <mat-option *ngIf="element.status !== '0'" [value]="element.uid">
                      {{ element.value }}
                    </mat-option>
                  </ng-container>
                </mat-select>
              </mat-form-field>
            </div>

            <div class="alternate-instructors-row">
              <mat-form-field style="flex: 1">
                <mat-label>Alternate Instructors</mat-label>
                <mat-select [(ngModel)]="editChanges.alternateInstructors" (ngModelChange)="onAlternatesChanged()" multiple>
                  <ng-container *ngFor="let element of sortedInstructors">
                    <mat-option *ngIf="element.status !== '0' && element.uid !== editChanges.instructor"
                                [value]="element.uid">
                      {{ element.value }}
                    </mat-option>
                  </ng-container>
                </mat-select>
              </mat-form-field>
              <button *ngIf="editChanges.alternateInstructors && editChanges.alternateInstructors.length > 0"
                      #rotationBtn
                      mat-icon-button type="button"
                      matTooltip="Rotation Order"
                      (click)="openRotationPopover(rotationBtn._elementRef.nativeElement)">
                <mat-icon>swap_vert</mat-icon>
              </button>
            </div>
          </div>

          <!-- Frequency -->
          <div class="edit-section">
            <label class="edit-section-label">Frequency</label>
            <div class="edit-fields-row">
              <mat-button-toggle-group [(ngModel)]="editChanges.repetitionUnits" (ngModelChange)="updateLivePreview()" appearance="standard">
                <mat-button-toggle value="weekly">Weekly</mat-button-toggle>
                <mat-button-toggle value="eow">Every Other Week</mat-button-toggle>
                <mat-button-toggle value="monthly">Monthly</mat-button-toggle>
              </mat-button-toggle-group>

              <mat-form-field *ngIf="editChanges.repetitionUnits === 'weekly' && editChanges.repetitionFrequency > 1" style="width: 140px">
                <mat-label>Every N weeks</mat-label>
                <input matInput type="number" min="1" max="12" [(ngModel)]="editChanges.repetitionFrequency" (ngModelChange)="updateLivePreview()">
              </mat-form-field>
            </div>
          </div>

          <!-- Stop Condition -->
          <div class="edit-section">
            <label class="edit-section-label">Stop Condition</label>
            <mat-radio-group [(ngModel)]="editChanges.endMode" (ngModelChange)="onEndModeChange()" class="stop-condition-group">
              <mat-radio-button value="date">Until date</mat-radio-button>
              <mat-radio-button value="enrollment">Until enrollment ends</mat-radio-button>
              <mat-radio-button value="indefinite">Indefinite</mat-radio-button>
            </mat-radio-group>

            <mat-form-field *ngIf="editChanges.endMode === 'date'" style="width: 180px; margin-top: 8px;">
              <mat-label>Expiration Date</mat-label>
              <input #endDateInput matInput type="text"
                     [value]="endDateDisplayValue"
                     readonly
                     (click)="openEndDatePicker()">
              <button matSuffix mat-icon-button type="button" (click)="openEndDatePicker()">
                <mat-icon>calendar_today</mat-icon>
              </button>
            </mat-form-field>

            <div *ngIf="editChanges.endMode === 'enrollment'" style="margin-top: 8px;">
              <mat-form-field style="width: 100%;">
                <mat-label>Enrollment</mat-label>
                <mat-select [(ngModel)]="editChanges.enrollmentId" (selectionChange)="onEnrollmentChange()">
                  <mat-option *ngFor="let e of enrollments" [value]="e.id[0]?.value">
                    {{ e.title[0]?.value }}
                    (Avail: {{ e.field_lesson_available[0]?.value }} of {{ e.field_enrollment_lesson_count[0]?.value }})
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <div *ngIf="enrollmentLessonsAvailable > 0" class="enrollment-end-info" style="color: #4caf50; font-size: 13px;">
                {{ enrollmentLessonsAvailable }} of {{ enrollmentLessonsTotal }} available ({{ enrollmentLessonsUsed }} used)
              </div>
              <div *ngIf="editChanges.enrollmentId && enrollmentLessonsAvailable === 0" class="enrollment-end-info" style="color: #f44336; font-size: 13px;">
                Enrollment fully used
              </div>
              <div *ngIf="enrollmentsLoading" style="font-size: 13px; color: #999;">Loading enrollments...</div>
            </div>
          </div>

          <!-- Notes -->
          <div class="edit-section">
            <mat-form-field style="width: 100%">
              <mat-label>Notes</mat-label>
              <textarea matInput [(ngModel)]="editChanges.notes" rows="2" placeholder="Optional notes about this series"></textarea>
            </mat-form-field>
          </div>

        </div>

        <!-- Right column: preview grid -->
        <div class="col-md-6">

          <!-- Event list preview -->
          <div class="preview-events-section">
            <div class="preview-events-header" style="display: flex; align-items: center; justify-content: space-between;">
              <span>
                <span *ngIf="previewLoading">Loading events...</span>
                <ng-container *ngIf="!previewLoading && livePreviewEvents.length > 0">
                  <span *ngIf="isConfigureMode">
                    Appointments to create ({{ activePreviewCount }}<span *ngIf="editChanges.endMode === 'indefinite'"> now, &infin; total</span><span *ngIf="excludedDates.size > 0">, {{ excludedDates.size }} skipped</span>)
                  </span>
                  <span *ngIf="!isConfigureMode && !hasStructuralChanges()">
                    Events ({{ livePreviewEvents.length }})
                  </span>
                  <span *ngIf="!isConfigureMode && hasStructuralChanges()">
                    Events ({{ activePreviewCount }}<span *ngIf="excludedDates.size > 0">, {{ excludedDates.size }} skipped</span>), will be regenerated
                  </span>
                </ng-container>
              </span>
              <button *ngIf="!previewLoading && livePreviewEvents.length > 0" type="button" class="preview-layout-toggle" (click)="togglePreviewLayout()" [title]="previewLayout === 'grid' ? 'Switch to list view' : 'Switch to grid view'">
                <mat-icon>{{ previewLayout === 'grid' ? 'view_list' : 'grid_view' }}</mat-icon>
              </button>
            </div>

            <div *ngIf="previewLoading" class="preview-events-loading">
              <mat-spinner diameter="20"></mat-spinner>
            </div>

            <!-- Change summary for simple edits -->
            <div *ngIf="!previewLoading && !hasStructuralChanges() && getChangeSummary().length > 0" class="change-summary">
              <strong>Changes to apply:</strong>
              <span *ngFor="let change of getChangeSummary()" class="change-summary-item">{{ change }}</span>
            </div>

            <div *ngIf="!previewLoading && livePreviewEvents.length > 0" class="preview-events-scroll">
              <!-- Event blocks grid -->
              <div class="event-blocks-grid" [class.event-blocks-list]="previewLayout === 'list'">
                <ng-container *ngFor="let evt of livePreviewEvents; let i = index; trackBy: trackEvent">
                  <div class="event-block" #eventCard
                       [class.event-block-selected]="isEventSelected(evt)"
                       [class.event-block-past]="isEventPast(evt.date)"
                       [class.event-block-posted]="isEventPosted(evt)"
                       [class.event-block-conflict]="isEventConflict(evt) && !isEventExcluded(evt)"
                       [class.event-block-excluded]="isEventExcluded(evt)"
                       (click)="selectEvent(evt, eventCard)">

                    <!-- Skip/restore button -->
                    <button *ngIf="isEditing && !isEventPosted(evt)"
                            type="button" class="event-block-skip-btn"
                            [class.restore]="isEventExcluded(evt)"
                            (click)="toggleExclude(evt, $event)"
                            [title]="isEventExcluded(evt) ? 'Restore' : 'Skip'">
                      <mat-icon>{{ isEventExcluded(evt) ? 'undo' : 'close' }}</mat-icon>
                    </button>

                    <div class="event-block-conflict-icon" *ngIf="isEventConflict(evt) && !isEventExcluded(evt)">
                      <mat-icon>error</mat-icon>
                    </div>

                    <!-- Override indicator -->
                    <div class="event-block-override-dot" *ngIf="hasDateOverride(evt.date) && !isEventExcluded(evt)"></div>

                    <div class="event-block-day">{{ evt.day }}</div>
                    <div class="event-block-datestr">{{ formatEventDate(evt.date) }}</div>
                    <div class="event-block-time">{{ evt.time }}</div>
                    <div class="event-block-instructor">{{ evt.instructor }}</div>
                    <div class="event-block-meta">{{ evt.duration }}m</div>
                    <div *ngIf="evt.statusTid && !isPendingStatus(evt.statusTid)"
                         class="event-block-status" [ngClass]="getStatusClass(evt.statusTid)">
                      {{ getStatusName(evt.statusTid) }}
                    </div>
                    <div class="event-block-hover-hint" *ngIf="!isEventSelected(evt) && !isEventExcluded(evt) && !isEventPosted(evt)">
                      <mat-icon>edit</mat-icon>
                    </div>
                    <div class="event-block-hover-hint" *ngIf="isEventSelected(evt)">
                      <mat-icon>close</mat-icon>
                    </div>
                  </div>
                </ng-container>
              </div>
            </div>
          </div>

        </div>

      </div>

    </div>

  </div>

</ng-template>

<!-- Configure Actions (new series, dialog only) -->
<mat-dialog-actions align="end" *ngIf="!embedded && isEditing && isConfigureMode">
  <button type="button" mat-raised-button [disabled]="isSaving" (click)="cancelEdit()">
    Cancel
  </button>
  <button *ngIf="hasConflicts" type="button" mat-raised-button color="warn"
          [disabled]="isSaving"
          (click)="generateIgnoringConflicts()">
    Generate Anyway
  </button>
  <button type="button" mat-raised-button color="primary"
          [disabled]="isSaving || (editChanges.days && editChanges.days.length === 0)"
          (click)="generateSeries()">
    {{ isSaving ? 'Generating...' : 'Generate' }}
  </button>
</mat-dialog-actions>

<!-- Edit Actions (existing series, dialog only) -->
<mat-dialog-actions align="end" *ngIf="!embedded && isEditing && !isConfigureMode">
  <button type="button" mat-raised-button [disabled]="isSaving" (click)="cancelEdit()">
    Cancel
  </button>
  <button type="button" mat-raised-button color="primary"
          [disabled]="isSaving || (editChanges.days && editChanges.days.length === 0)"
          (click)="saveEdit()">
    <span *ngIf="isSaving">Saving...</span>
    <span *ngIf="!isSaving && !hasStructuralChanges()">Save All Changes</span>
    <span *ngIf="!isSaving && hasStructuralChanges()">Regenerate Series</span>
  </button>
</mat-dialog-actions>

<!-- Action buttons for summary view (dialog only) -->
<mat-dialog-actions align="end" *ngIf="!embedded && series && !isLoading && !isEditing && !showDeleteSingleConfirm && !showDeactivateConfirm">
  <button type="button" mat-raised-button color="warn" (click)="startDeactivate()">
    <mat-icon>delete_forever</mat-icon> Delete Series
  </button>
  <button type="button" mat-raised-button color="primary" (click)="startEdit()">
    <mat-icon>edit</mat-icon> Edit Series
  </button>
</mat-dialog-actions>

<!-- Event edit popover (rendered in CDK overlay) -->
<ng-template #eventPopoverTpl>
  <div class="event-popover" (click)="$event.stopPropagation()">
    <div class="event-popover-header"
         [class.event-popover-header-conflict]="selectedEvent && isEventConflict(selectedEvent)">
      <strong>{{ selectedEvent?.day }}, {{ formatEventDate(selectedEvent?.date) }}</strong>
      <button mat-icon-button (click)="cancelSingleEvent()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div *ngIf="selectedEvent && isEventConflict(selectedEvent)" class="event-popover-conflict-info">
      <mat-icon>warning</mat-icon>
      <span>{{ getEventConflict(selectedEvent)?.message || 'Schedule conflict detected' }}</span>
    </div>

    <!-- Edit fields (hidden during delete confirm) -->
    <ng-container *ngIf="!showPopoverDeleteConfirm">
      <div class="event-popover-body">
        <mat-form-field class="event-popover-field">
          <mat-label>Time</mat-label>
          <mat-select [(ngModel)]="editTime" (ngModelChange)="onEditTimeChange()">
            <mat-option *ngFor="let t of timeOptions" [value]="t.value">{{ t.label }}</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field class="event-popover-field">
          <mat-label>Instructor</mat-label>
          <mat-select [(ngModel)]="editInstructor" (ngModelChange)="onEditInstructorChange()">
            <ng-container *ngFor="let element of sortedInstructors">
              <mat-option *ngIf="element.status !== '0'" [value]="element.uid">
                {{ element.value }}
              </mat-option>
            </ng-container>
          </mat-select>
        </mat-form-field>
        <mat-form-field class="event-popover-field" *ngIf="selectedEvent?.id">
          <mat-label>Duration</mat-label>
          <mat-select [(ngModel)]="singleEventChanges.duration">
            <mat-option *ngFor="let element of _fieldsService.durations"
                        [value]="element.value">{{ element.value }}</mat-option>
          </mat-select>
        </mat-form-field>

        <app-schedule-conflicts
          #popoverConflicts
          [compact]="true"
          [dateTime]="editDateTime"
          [instructor]="editInstructor || singleEventChanges.instructor"
          [duration]="editChanges.duration || series?.duration"
          [entityId]="selectedEvent?.id ? '' + selectedEvent.id : ''"
          [excludeStandingSeriesId]="series?.id ? '' + series.id : ''"
          (dateTimeChange)="onPopoverAlternativeTime($event)">
        </app-schedule-conflicts>
      </div>
      <div class="event-popover-actions">
        <button mat-raised-button color="primary" [disabled]="isSavingSingle"
                (click)="saveSingleEvent()">
          {{ isSavingSingle ? 'Saving...' : 'Save' }}
        </button>
        <button mat-stroked-button [disabled]="isSavingSingle"
                (click)="cancelSingleEvent()">Cancel</button>
        <button *ngIf="selectedEvent?.id" mat-icon-button color="warn" [disabled]="isSavingSingle"
                (click)="startDeleteSingleFromPopover()" matTooltip="Delete this event">
          <mat-icon>delete_outline</mat-icon>
        </button>
      </div>
    </ng-container>

    <!-- Delete confirmation (shown inside popover) -->
    <ng-container *ngIf="showPopoverDeleteConfirm">
      <div class="event-popover-delete-confirm">
        <div class="event-popover-delete-warning">
          <mat-icon>warning</mat-icon>
          <span>Delete this event? This cannot be undone.</span>
        </div>
        <div class="event-popover-actions">
          <button mat-raised-button color="warn" [disabled]="isDeletingSingle"
                  (click)="confirmDeleteSingleFromPopover()">
            {{ isDeletingSingle ? 'Deleting...' : 'Delete' }}
          </button>
          <button mat-stroked-button [disabled]="isDeletingSingle"
                  (click)="cancelDeleteSingleFromPopover()">Cancel</button>
        </div>
      </div>
    </ng-container>
  </div>
</ng-template>

<!-- CDK Overlay popover for rotation order -->
<ng-template #rotationPopoverTpl>
  <div class="event-popover" (click)="$event.stopPropagation()">
    <div class="event-popover-header">
      <strong>Rotation Order</strong>
      <span style="font-size: 0.8em; color: #888; margin-left: 8px;">Drag to reorder</span>
    </div>
    <div class="rotation-order-list" cdkDropList (cdkDropListDropped)="onRotationReorder($event)">
      <div class="rotation-order-item rotation-order-primary">
        <span class="rotation-order-number">1</span>
        <span class="rotation-order-name">{{ getInstructorName(editChanges.instructor) }}</span>
        <span class="rotation-order-badge">Primary</span>
      </div>
      <div *ngFor="let uid of editChanges.alternateInstructors; let i = index"
           class="rotation-order-item" cdkDrag>
        <mat-icon class="rotation-order-handle" cdkDragHandle>drag_indicator</mat-icon>
        <span class="rotation-order-number">{{ i + 2 }}</span>
        <span class="rotation-order-name">{{ getInstructorName(uid) }}</span>
        <button mat-icon-button class="rotation-order-remove"
                (click)="removeAlternate(i)" matTooltip="Remove">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>
  </div>
</ng-template>
